import {
  Button,
  DatePicker,
  Form, Input,
  Select,
  Spin,
} from 'antd';
import moment from 'moment';
import {
  bool,
  func,
  object,
} from 'prop-types';
import {
  useEffect,
  useRef,
  useState,
} from 'react';

import { getInfo, getRegionsAndCities } from 'actions/general';

import { getMarket } from 'utils/helpers';
import { EditSVG } from 'components/iconsSVG/edit';
import { ChevronSVG } from 'components/iconsSVG/chevron';
import { AutoComplete, PhoneInput } from 'components/base';
import FormInput from 'components/formInput';
import { useI18n } from 'context/i18n';

import { getConfigByLocale } from 'utils/getConfigByLocale';

import { useValidate } from 'hooks';
import styles from './style.module.css';

const { Option } = Select;

const HomePickUp = ({
  order, changeAddress, handleNext, withCodes, startEmpty, onlyLatinAlphabet, onChangeDate,
}) => {
  const {
    locale, t, localeConfigs,
  } = useI18n();
  const validate = useValidate();
  const [ isLoading, setIsLoading ] = useState(true);
  const [ isEditing, setIsEditing ] = useState(false);
  const [ info, setInfo ] = useState(null);
  const [ modifiedAddress, setModifiedAddress ] = useState(null);

  const [ HomePickUpForm ] = Form.useForm();
  const [ DateForm ] = Form.useForm();
  const homePickUpRef = useRef(null);

  const localeFormat = getConfigByLocale(locale, localeConfigs);

  const { receiver, applicant } = order;

  // lógica para usar receiver o applicant
  const pickUpData = modifiedAddress || receiver || applicant;

  const {
    address, postalCode, city, country, telephone, region,
  } = pickUpData;

  const splittedRegion = region.split('-');

  if (splittedRegion.length > 1) {
    [ , pickUpData.region ] = splittedRegion;
  }

  useEffect(() => {
    const getExtraInfoHandler = async () => {
      const { phonePrefixes } = await getInfo({ profile: locale });
      const { cities, regions } = await getRegionsAndCities(locale);

      setInfo({
        phonePrefixes,
        cities,
        regions,
      });
      const newPickupData = { ...pickUpData };
      const regionId = regions.filter((regionObj) => regionObj.name === pickUpData.region);
      if (regionId.length && withCodes) {
        newPickupData.region = regionId[0].id;
      }
      if (!startEmpty) {
        changeAddress({
          ...newPickupData,
        });
      } else {
        pickUpData.address = '';
        pickUpData.postalCode = '';
        pickUpData.city = '';
        pickUpData.address = '';
        pickUpData.region = '';
        pickUpData.telephone = '';
        setIsEditing(true);
      }
    };

    getExtraInfoHandler();
  }, []);

  useEffect(() => {
    if (info !== null) {
      setIsLoading(false);
    }
  }, [ info ]);

  const handleFinishEdit = (values) => {
    const newPickupData = { ...values };
    const regionId = info.regions.filter((regionObj) => regionObj.name === newPickupData.region);
    if (regionId.length && withCodes) {
      newPickupData.region = regionId[0].id;
    }
    setModifiedAddress(newPickupData);
    changeAddress({
      ...newPickupData,
      edited: true,
    });
    setIsEditing(false);
  };

  const setInvalidDate = () => {
    DateForm.setFields([
      {
        name: 'date',
        errors: [ t('validation', 'invalid.date', 'Please, select a valid date') ],
      },
    ]);
    return false;
  };

  // Can not select days before today and today
  const disabledDate = (current) => {
    let isDateDisabled = false;
    const today = moment();
    const tomorrow = today.add(1, 'days');
    const dayOfTheWeekForCurrent = current.day();

    if (current) {
      if (
        current < tomorrow.endOf('day') ||
        [ 0, 6 ].includes(dayOfTheWeekForCurrent) // weekend (6 sat, 0 sun)
      ) {
        isDateDisabled = true;
      }
    }

    return isDateDisabled;
  };

  const validateDate = () => {
    const realValue = DateForm.getFieldValue('date');

    if (realValue !== undefined) {
      const formattedValue = moment(realValue, localeFormat.dateFormat);

      if (formattedValue._isValid) {
        const isFutureDate = formattedValue.isAfter(new Date(), 'day');

        if (isFutureDate) {
          DateForm.setFields([
            {
              name: 'date',
              errors: [],
              value: formattedValue,
            },
          ]);
          const newPickupData = { ...pickUpData };
          const regionId = info.regions.filter((regionObj) => regionObj.name === pickUpData.region);
          if (regionId.length && withCodes) {
            newPickupData.region = regionId[0].id;
          }
          onChangeDate();
          changeAddress({
            ...newPickupData,
            returnDate: formattedValue.format('YYYY-MM-DD'),
            edited: modifiedAddress !== null,
          });
          return true;
        }

        return setInvalidDate();
      }

      DateForm.setFields([
        {
          name: 'date',
          errors: [ t('newsletter', 'error.fecha.incorrecta.registro') ],
        },
      ]);
      return false;
    }

    return setInvalidDate();
  };

  const handleComplete = () => {
    let anyError = false;

    if (isEditing) {
      HomePickUpForm.getFieldsError();
    }

    const isDateValid = validateDate();

    if (!isDateValid) {
      anyError = true;
    }

    if (!anyError) {
      handleNext();
    }
  };

  const regionIsNumeric = (/^\d+$/).test(region);
  const currentRegionName = regionIsNumeric ? info.regions.filter((item) => item.id === region)[0].name : region;

  return (<Spin spinning={isLoading}>
    <span className={styles.divider}>
      <ChevronSVG />
    </span>
    <div className={styles.homePickUpTitleWrapper}>
      <h2 className={styles.homePickUpTitle}>{t('mis.pedidos', 'home.pick.up.information', '***Home Collection Information')}</h2>
      <p className={styles.homePickUpSubtitle}>{t('mis.pedidos', 'to.make.the.exchanging.process.the.easiest.for.you', '**')}</p>
    </div>

    {
      isEditing ?
      <Form
        form={HomePickUpForm}
        layout="vertical"
        ref={homePickUpRef}
        name="HomePickUp"
        onFinish={handleFinishEdit}
        onFinishFailed={(values) => console.log(values)}
        requiredMark='*'
        scrollToFirstError
        autoComplete='true'
        validateTrigger={[ 'onBlur' ]}
        className={styles.homePickUpForm}
        initialValues={{
          ...pickUpData,
          preffix: getMarket(locale),
        }}
      >
        <FormInput
          name="address"
          rules={!onlyLatinAlphabet ? validate('address') : validate('addressLatinAlphabet')}
          floatLabel={t('compra.formulario', 'direccion')}
        >
          <Input placeholder="" />
        </FormInput>
        <FormInput
          name="country"
          rules={validate('country')}
          floatLabel={t('compra.formulario', 'pais')}
        >
          <Select
            autoComplete="country"
            disabled
            showArrow={false}
            virtual={false}
            dropdownClassName={styles.dropdown}
          >
            <Option value={getMarket(locale)}>{country}</Option>
          </Select>
        </FormInput>
        <FormInput
          name="region"
          rules={validate('region')}
          floatLabel={t('compra.formulario', 'provincia')}
        >
          <Select
            showSearch
            optionFilterProp="children"
            notFoundContent=""
            filterOption={true}
            suffixIcon ={<ChevronSVG />}
            virtual={false}
            dropdownClassName={styles.dropdown}
          >
            {
              info && info.regions && <>
              {
                info.regions.map((item, index) => <Option key={`region-${index}`} value={item.id}>{item.name}</Option>)
              }
              </>
            }
          </Select>
        </FormInput>
        <FormInput
          rules={validate('postalCode')}
          name="postalCode"
          floatLabel={t('compra.formulario', 'cp')}
        >
          <Input placeholder="" />
        </FormInput>
        <FormInput
          name="city"
          rules={validate('cityOrder')}
          floatLabel={t('compra.formulario', 'ciudad')}
        >
          <AutoComplete
            inputValue={city}
            id='city-input'
            baseOptions={info !== null && info.cities ? info.cities.map((item) => ({ value: item })) : []}
            dropdownClassName={styles.dropdown}
          />
        </FormInput>
        <PhoneInput className={styles.phoneWrapper} info={info} />
        <Button className={styles.homePickUpSubmit} htmlType="submit">
          {t('compra.formulario', 'save.this.address', '**Save this address')}
        </Button>
      </Form> :
      <div className={styles.homePickUpResumeWrapper}>
        <div className={styles.homePickUpData}>
          <p>{address}</p>
          <p>
            <span>{postalCode}</span><span>{city}</span>
          </p>
          <p>
            {currentRegionName}
          </p>
          <p>{country}</p>
          <p className={styles.homePickUpTelephone}>
            <span>{t('generico', 'telefono', '**Telephone')}: {telephone}</span>
          </p>
        </div>
        <div className={styles.homePickUpEditWrapper}>
          <Button className={styles.homePickUpEditButton} icon={<EditSVG />} onClick={() => {
            setIsEditing(true);
          }} />
        </div>
      </div>
    }

    <Form
      form={DateForm}
      onFinish={handleComplete}
      fields={[ 'date' ]}
    >
      <p className={styles.collectionDateLabel}>{t('compra.formulario', 'pick.up.date')}</p>
      <div className={styles.dateForPickUpWrapper}>
        <FormInput
          name="date"
          required
        >
          <DatePicker
            onChange={validateDate}
            locale={localeFormat}
            format={localeFormat.dateFormat}
            onBlur={validateDate}
            disabledDate={disabledDate}
          />
        </FormInput>
      </div>
      <div className={styles.nextWrapper}>
        <Button type="primary" htmlType="submit" className={styles.nextStepBtn}>{t('pedido', 'pedido.devoluciones.siguiente.paso', 'Next')}</Button>
      </div>
    </Form>
  </Spin>);
};

HomePickUp.propTypes = {
  order: object,
  changeAddress: func,
  handleNext: func,
  withCodes: bool,
  startEmpty: bool,
  onlyLatinAlphabet: bool,
  onChangeDate: func,
};

HomePickUp.defaultProps = {
  order: {},
  withCodes: false,
  startEmpty: false,
  onlyLatinAlphabet: false,
  onChangeDate: () => true,
};

export default HomePickUp;
